/**
 * FikoSPHGPUHesap - Main WebGPU compute pipeline manager for SPH fluid simulation
 * Handles all GPU buffers, bind groups, and compute passes for the simulation
 */

import type { SPHSimulasyonConfig, Vec3 } from '../FikoSPHParametreler';
import { SolverTipi, GPU_STRUCT_BOYUTLARI } from '../FikoSPHParametreler';
import type { ISPHSolver, GPUSolverBuffers } from './ISPHSolver';
import { SolverFabrika, hesaplaWorkgroupSayisi } from './ISPHSolver';
import { ElemanTipi, ElemanYonetici, GPU_MAX_ELEMAN_SAYILARI } from '../elemanlar/ISPHEleman';
import {
  SPH_STRUCT_TANIMLARI,
  SPH_KERNEL_FONKSIYONLARI,
  SPH_GRID_YARDIMCI,
  SPH_ORTAK_BINDING_TANIMLARI,
  buildShader
} from './sph-ortak-shaders.wgsl';
import {
  GRID_HASH_PARTICLES,
  GRID_CLEAR_COUNTS,
  GRID_COUNT_PARTICLES,
  GRID_PREFIX_SUM_LOCAL,
  GRID_PREFIX_SUM_BLOCKS,
  GRID_PREFIX_SUM_ADD,
  GRID_COPY_TO_OFFSET,
  GRID_SCATTER,
  GRID_NEIGHBOR_HELPER
} from './sph-grid-shaders.wgsl';
import { SDF_COLLISION_PASS } from './sph-sdf-shaders.wgsl';

/** Simulation bounds */
interface SimBounds {
  min: Vec3;
  max: Vec3;
}

/** GPU Hesap initialization config */
interface GPUHesapConfig {
  device: GPUDevice;
  maxParticle: number;
  simBounds: SimBounds;
  smoothingRadius: number;
}

/** Element type to GPU struct key mapping */
const ELEMAN_TIPI_TO_STRUCT_KEY: Record<ElemanTipi, keyof typeof GPU_STRUCT_BOYUTLARI> = {
  [ElemanTipi.DOKUM_GIRISI]: 'DOKUM_GIRISI',
  [ElemanTipi.HAVA_DELIGI]: 'HAVA_DELIGI',
  [ElemanTipi.BESLEYICI]: 'BESLEYICI',
  [ElemanTipi.FILTRE]: 'FILTRE',
  [ElemanTipi.ENGEL]: 'ENGEL',
  [ElemanTipi.CIKIS]: 'CIKIS',
  [ElemanTipi.SANAL_SENSOR]: 'SENSOR',
  [ElemanTipi.TERMAL_ELEMAN]: 'TERMAL_ELEMAN'
};

/**
 * Main GPU compute pipeline manager for SPH simulation
 */
export class FikoSPHGPUHesap {
  private device: GPUDevice | null = null;

  // Particle buffers (Structure of Arrays)
  private positionMassBuffer: GPUBuffer | null = null;
  private velocityAgeBuffer: GPUBuffer | null = null;
  private densityMiscBuffer: GPUBuffer | null = null;
  private forceVmaxBuffer: GPUBuffer | null = null;

  // Grid spatial hashing buffers
  private cellCountBuffer: GPUBuffer | null = null;
  private prefixSumBuffer: GPUBuffer | null = null;
  private cellOffsetBuffer: GPUBuffer | null = null;
  private sortedIndexBuffer: GPUBuffer | null = null;
  private cellHashBuffer: GPUBuffer | null = null;
  private blockSumsBuffer: GPUBuffer | null = null;

  // Element buffers (one per element type)
  private elemanBuffers: Map<ElemanTipi, GPUBuffer> = new Map();
  private elemanDirtyFlags: Map<ElemanTipi, boolean> = new Map();

  // Uniform buffer
  private uniformBuffer: GPUBuffer | null = null;
  private uniformData: ArrayBuffer = new ArrayBuffer(256);

  // Indirect dispatch and atomics
  private indirectDispatchBuffer: GPUBuffer | null = null;
  private atomicsBuffer: GPUBuffer | null = null;

  // Sensor and statistics buffers
  private sensorResultBuffer: GPUBuffer | null = null;
  private sensorReadbackBuffer: GPUBuffer | null = null;
  private statisticsBuffer: GPUBuffer | null = null;
  private statisticsReadbackBuffer: GPUBuffer | null = null;

  // Heatmap visualization
  private heatmapBuffer: GPUBuffer | null = null;

  // Thermal simulation buffers
  private particleSicaklikBuffer: GPUBuffer | null = null;
  private particleFazBuffer: GPUBuffer | null = null;

  // Gas simulation buffers
  private kabarcikBoyutBuffer: GPUBuffer | null = null;
  private kabarcikFlagsBuffer: GPUBuffer | null = null;

  // SDF collision buffer (from KapYonetici)
  private sdfBuffer: GPUBuffer | null = null;
  private sdfInfoBuffer: GPUBuffer | null = null;
  private sdfBGL: GPUBindGroupLayout | null = null;
  private sdfBG: GPUBindGroup | null = null;

  // Bind group layouts
  private uniformBGL: GPUBindGroupLayout | null = null;
  private particleBGL: GPUBindGroupLayout | null = null;
  private gridBGL: GPUBindGroupLayout | null = null;

  // Bind groups
  private uniformBG: GPUBindGroup | null = null;
  private particleBG: GPUBindGroup | null = null;
  private gridBG: GPUBindGroup | null = null;

  // Grid pipelines (8 passes)
  private gridHashPipeline: GPUComputePipeline | null = null;
  private gridClearPipeline: GPUComputePipeline | null = null;
  private gridCountPipeline: GPUComputePipeline | null = null;
  private gridPrefixSumLocalPipeline: GPUComputePipeline | null = null;
  private gridPrefixSumBlocksPipeline: GPUComputePipeline | null = null;
  private gridPrefixSumAddPipeline: GPUComputePipeline | null = null;
  private gridCopyOffsetPipeline: GPUComputePipeline | null = null;
  private gridScatterPipeline: GPUComputePipeline | null = null;

  // Integration pipelines
  private integratePipeline: GPUComputePipeline | null = null;
  private sdfCollisionPipeline: GPUComputePipeline | null = null;
  private cflPipeline: GPUComputePipeline | null = null;

  // Active solver
  private aktivSolver: ISPHSolver | null = null;
  private solverBuffers: GPUSolverBuffers | null = null;
  private solverBindGroup: GPUBindGroup | null = null;

  // State
  private maxParticle: number = 0;
  private gridDim: [number, number, number] = [64, 64, 64];
  private totalGridCells: number = 0;
  private hazir: boolean = false;

  // Shader cache
  private shaderCache: Map<string, GPUShaderModule> = new Map();

  /**
   * Initialize the GPU compute system
   */
  async basla(config: GPUHesapConfig): Promise<boolean> {
    try {
      this.device = config.device;
      this.maxParticle = config.maxParticle;

      // Calculate grid dimensions
      const boundsSize = {
        x: config.simBounds.max.x - config.simBounds.min.x,
        y: config.simBounds.max.y - config.simBounds.min.y,
        z: config.simBounds.max.z - config.simBounds.min.z
      };

      const cellSize = config.smoothingRadius * 2.0;
      this.gridDim = [
        Math.min(256, Math.ceil(boundsSize.x / cellSize)),
        Math.min(256, Math.ceil(boundsSize.y / cellSize)),
        Math.min(256, Math.ceil(boundsSize.z / cellSize))
      ];
      this.totalGridCells = this.gridDim[0] * this.gridDim[1] * this.gridDim[2];

      // Initialize uniform data
      this.uniformData = new ArrayBuffer(256);

      // Create all GPU resources
      this.olusturBufferlar(this.maxParticle);
      this.olusturBindGroupLayoutlar();
      this.olusturBindGrouplar();
      await this.olusturGridPipelines();
      await this.olusturIntegrationPipelines();

      this.hazir = true;
      return true;
    } catch (error) {
      console.error('FikoSPHGPUHesap baslatma hatasi:', error);
      return false;
    }
  }

  /**
   * Create all GPU buffers
   */
  private olusturBufferlar(maxParticle: number): void {
    if (!this.device) return;

    const bufferUsage = GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC;

    // Particle buffers (SoA layout, each 16 bytes per particle)
    this.positionMassBuffer = this.device.createBuffer({
      label: 'SPH Position-Mass Buffer',
      size: maxParticle * 16,
      usage: bufferUsage
    });

    this.velocityAgeBuffer = this.device.createBuffer({
      label: 'SPH Velocity-Age Buffer',
      size: maxParticle * 16,
      usage: bufferUsage
    });

    this.densityMiscBuffer = this.device.createBuffer({
      label: 'SPH Density-Misc Buffer',
      size: maxParticle * 16,
      usage: bufferUsage
    });

    this.forceVmaxBuffer = this.device.createBuffer({
      label: 'SPH Force-Vmax Buffer',
      size: maxParticle * 16,
      usage: bufferUsage
    });

    // Grid buffers
    this.cellCountBuffer = this.device.createBuffer({
      label: 'SPH Cell Count Buffer',
      size: this.totalGridCells * 4,
      usage: bufferUsage
    });

    this.prefixSumBuffer = this.device.createBuffer({
      label: 'SPH Prefix Sum Buffer',
      size: this.totalGridCells * 4,
      usage: bufferUsage
    });

    this.cellOffsetBuffer = this.device.createBuffer({
      label: 'SPH Cell Offset Buffer',
      size: this.totalGridCells * 4,
      usage: bufferUsage
    });

    this.sortedIndexBuffer = this.device.createBuffer({
      label: 'SPH Sorted Index Buffer',
      size: maxParticle * 4,
      usage: bufferUsage
    });

    this.cellHashBuffer = this.device.createBuffer({
      label: 'SPH Cell Hash Buffer',
      size: maxParticle * 4,
      usage: bufferUsage
    });

    const numBlocks = Math.ceil(this.totalGridCells / 256);
    this.blockSumsBuffer = this.device.createBuffer({
      label: 'SPH Block Sums Buffer',
      size: Math.max(256, numBlocks * 4),
      usage: bufferUsage
    });

    // Element buffers - iterate over all ElemanTipi values
    const allElemanTipleri = Object.values(ElemanTipi);
    for (const tip of allElemanTipleri) {
      const structKey = ELEMAN_TIPI_TO_STRUCT_KEY[tip];
      const maxCount = GPU_MAX_ELEMAN_SAYILARI[tip];
      const structSize = GPU_STRUCT_BOYUTLARI[structKey];

      const buffer = this.device.createBuffer({
        label: `SPH Element Buffer - ${structKey}`,
        size: maxCount * structSize,
        usage: bufferUsage
      });

      this.elemanBuffers.set(tip, buffer);
      this.elemanDirtyFlags.set(tip, false);
    }

    // Uniform buffer
    this.uniformBuffer = this.device.createBuffer({
      label: 'SPH Uniform Buffer',
      size: 256,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    });

    // Indirect dispatch buffer
    this.indirectDispatchBuffer = this.device.createBuffer({
      label: 'SPH Indirect Dispatch Buffer',
      size: 12,
      usage: GPUBufferUsage.INDIRECT | GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC
    });

    // Atomics buffer
    this.atomicsBuffer = this.device.createBuffer({
      label: 'SPH Atomics Buffer',
      size: 32,
      usage: bufferUsage
    });

    // Sensor buffers
    this.sensorResultBuffer = this.device.createBuffer({
      label: 'SPH Sensor Result Buffer',
      size: 64 * 16, // 64 sensors max, 16 bytes each
      usage: bufferUsage
    });

    this.sensorReadbackBuffer = this.device.createBuffer({
      label: 'SPH Sensor Readback Buffer',
      size: 64 * 16,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST
    });

    // Statistics buffers
    this.statisticsBuffer = this.device.createBuffer({
      label: 'SPH Statistics Buffer',
      size: 64,
      usage: bufferUsage
    });

    this.statisticsReadbackBuffer = this.device.createBuffer({
      label: 'SPH Statistics Readback Buffer',
      size: 64,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST
    });

    // Heatmap buffer
    this.heatmapBuffer = this.device.createBuffer({
      label: 'SPH Heatmap Buffer',
      size: maxParticle * 4,
      usage: bufferUsage
    });

    // Thermal buffers
    this.particleSicaklikBuffer = this.device.createBuffer({
      label: 'SPH Particle Temperature Buffer',
      size: maxParticle * 4,
      usage: bufferUsage
    });

    this.particleFazBuffer = this.device.createBuffer({
      label: 'SPH Particle Phase Buffer',
      size: maxParticle * 4,
      usage: bufferUsage
    });

    // Gas buffers
    this.kabarcikBoyutBuffer = this.device.createBuffer({
      label: 'SPH Bubble Size Buffer',
      size: maxParticle * 4,
      usage: bufferUsage
    });

    this.kabarcikFlagsBuffer = this.device.createBuffer({
      label: 'SPH Bubble Flags Buffer',
      size: maxParticle * 4,
      usage: bufferUsage
    });
  }

  /**
   * Create bind group layouts
   */
  private olusturBindGroupLayoutlar(): void {
    if (!this.device) return;

    // Group 0: Uniform buffer
    this.uniformBGL = this.device.createBindGroupLayout({
      label: 'SPH Uniform BGL',
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: 'uniform' }
        }
      ]
    });

    // Group 1: Particle buffers
    this.particleBGL = this.device.createBindGroupLayout({
      label: 'SPH Particle BGL',
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }, // positionMass
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }, // velocityAge
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }, // densityMisc
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }, // forceVmax
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }, // atomics
        { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }  // heatmap
      ]
    });

    // Group 2: Grid buffers
    this.gridBGL = this.device.createBindGroupLayout({
      label: 'SPH Grid BGL',
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }, // cellCount
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }, // prefixSum
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }, // sortedIndex
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }, // cellHash
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }, // cellOffset
        { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }  // blockSums
      ]
    });
  }

  /**
   * Create bind groups
   */
  private olusturBindGrouplar(): void {
    if (!this.device || !this.uniformBGL || !this.particleBGL || !this.gridBGL) return;

    this.uniformBG = this.device.createBindGroup({
      label: 'SPH Uniform BG',
      layout: this.uniformBGL,
      entries: [
        { binding: 0, resource: { buffer: this.uniformBuffer! } }
      ]
    });

    this.particleBG = this.device.createBindGroup({
      label: 'SPH Particle BG',
      layout: this.particleBGL,
      entries: [
        { binding: 0, resource: { buffer: this.positionMassBuffer! } },
        { binding: 1, resource: { buffer: this.velocityAgeBuffer! } },
        { binding: 2, resource: { buffer: this.densityMiscBuffer! } },
        { binding: 3, resource: { buffer: this.forceVmaxBuffer! } },
        { binding: 4, resource: { buffer: this.atomicsBuffer! } },
        { binding: 5, resource: { buffer: this.heatmapBuffer! } }
      ]
    });

    this.gridBG = this.device.createBindGroup({
      label: 'SPH Grid BG',
      layout: this.gridBGL,
      entries: [
        { binding: 0, resource: { buffer: this.cellCountBuffer! } },
        { binding: 1, resource: { buffer: this.prefixSumBuffer! } },
        { binding: 2, resource: { buffer: this.sortedIndexBuffer! } },
        { binding: 3, resource: { buffer: this.cellHashBuffer! } },
        { binding: 4, resource: { buffer: this.cellOffsetBuffer! } },
        { binding: 5, resource: { buffer: this.blockSumsBuffer! } }
      ]
    });
  }

  /**
   * Create grid spatial hashing pipelines
   */
  private async olusturGridPipelines(): Promise<void> {
    if (!this.device || !this.uniformBGL || !this.particleBGL || !this.gridBGL) return;

    const pipelineLayout = this.device.createPipelineLayout({
      label: 'SPH Grid Pipeline Layout',
      bindGroupLayouts: [this.uniformBGL, this.particleBGL, this.gridBGL]
    });

    // Hash particles
    const hashShader = this.derleShader('gridHash',
      SPH_STRUCT_TANIMLARI + SPH_GRID_YARDIMCI + GRID_HASH_PARTICLES);
    this.gridHashPipeline = this.device.createComputePipeline({
      label: 'SPH Grid Hash Pipeline',
      layout: pipelineLayout,
      compute: { module: hashShader, entryPoint: 'hashParticles' }
    });

    // Clear counts
    const clearShader = this.derleShader('gridClear',
      SPH_STRUCT_TANIMLARI + SPH_GRID_YARDIMCI + GRID_CLEAR_COUNTS);
    this.gridClearPipeline = this.device.createComputePipeline({
      label: 'SPH Grid Clear Pipeline',
      layout: pipelineLayout,
      compute: { module: clearShader, entryPoint: 'clearCounts' }
    });

    // Count particles
    const countShader = this.derleShader('gridCount',
      SPH_STRUCT_TANIMLARI + SPH_GRID_YARDIMCI + GRID_COUNT_PARTICLES);
    this.gridCountPipeline = this.device.createComputePipeline({
      label: 'SPH Grid Count Pipeline',
      layout: pipelineLayout,
      compute: { module: countShader, entryPoint: 'countParticles' }
    });

    // Prefix sum local
    const prefixLocalShader = this.derleShader('gridPrefixLocal',
      SPH_STRUCT_TANIMLARI + SPH_GRID_YARDIMCI + GRID_PREFIX_SUM_LOCAL);
    this.gridPrefixSumLocalPipeline = this.device.createComputePipeline({
      label: 'SPH Grid Prefix Sum Local Pipeline',
      layout: pipelineLayout,
      compute: { module: prefixLocalShader, entryPoint: 'prefixSumLocal' }
    });

    // Prefix sum blocks
    const prefixBlocksShader = this.derleShader('gridPrefixBlocks',
      SPH_STRUCT_TANIMLARI + SPH_GRID_YARDIMCI + GRID_PREFIX_SUM_BLOCKS);
    this.gridPrefixSumBlocksPipeline = this.device.createComputePipeline({
      label: 'SPH Grid Prefix Sum Blocks Pipeline',
      layout: pipelineLayout,
      compute: { module: prefixBlocksShader, entryPoint: 'prefixSumBlocks' }
    });

    // Prefix sum add
    const prefixAddShader = this.derleShader('gridPrefixAdd',
      SPH_STRUCT_TANIMLARI + SPH_GRID_YARDIMCI + GRID_PREFIX_SUM_ADD);
    this.gridPrefixSumAddPipeline = this.device.createComputePipeline({
      label: 'SPH Grid Prefix Sum Add Pipeline',
      layout: pipelineLayout,
      compute: { module: prefixAddShader, entryPoint: 'prefixSumAdd' }
    });

    // Copy to offset
    const copyOffsetShader = this.derleShader('gridCopyOffset',
      SPH_STRUCT_TANIMLARI + SPH_GRID_YARDIMCI + GRID_COPY_TO_OFFSET);
    this.gridCopyOffsetPipeline = this.device.createComputePipeline({
      label: 'SPH Grid Copy Offset Pipeline',
      layout: pipelineLayout,
      compute: { module: copyOffsetShader, entryPoint: 'copyToOffset' }
    });

    // Scatter particles
    const scatterShader = this.derleShader('gridScatter',
      SPH_STRUCT_TANIMLARI + SPH_GRID_YARDIMCI + GRID_SCATTER);
    this.gridScatterPipeline = this.device.createComputePipeline({
      label: 'SPH Grid Scatter Pipeline',
      layout: pipelineLayout,
      compute: { module: scatterShader, entryPoint: 'scatterParticles' }
    });
  }

  /**
   * Create integration pipelines
   */
  private async olusturIntegrationPipelines(): Promise<void> {
    if (!this.device || !this.uniformBGL || !this.particleBGL || !this.gridBGL) return;

    const pipelineLayout = this.device.createPipelineLayout({
      label: 'SPH Integration Pipeline Layout',
      bindGroupLayouts: [this.uniformBGL, this.particleBGL, this.gridBGL]
    });

    // Integration shader
    const integrateCode = `
${SPH_STRUCT_TANIMLARI}

@compute @workgroup_size(256)
fn integrate(@builtin(global_invocation_id) gid: vec3<u32>) {
  let idx = gid.x;
  if (idx >= params.activeCount) { return; }

  let force = forceVmax[idx].xyz;
  let invMass = positionMass[idx].w;

  // Update velocity: v += dt * (F * invMass + gravity)
  var vel = velocityAge[idx].xyz;
  let gravity = vec3<f32>(params.gravity_x, params.gravity_y, params.gravity_z);
  vel += params.dt * (force * invMass + gravity);

  // Update position
  var pos = positionMass[idx].xyz;
  pos += params.dt * vel;

  // Simple box boundary clamp (will be replaced by SDF collision later)
  let bmin = vec3<f32>(params.gridOrigin_x, params.gridOrigin_y, params.gridOrigin_z);
  let bmax = bmin + vec3<f32>(
    f32(params.gridDim_x) * params.gridCellSize,
    f32(params.gridDim_y) * params.gridCellSize,
    f32(params.gridDim_z) * params.gridCellSize
  );
  let eps = params.smoothingRadius * 0.5;

  if (pos.x < bmin.x + eps) { pos.x = bmin.x + eps; vel.x *= -params.boundaryDamping; }
  if (pos.x > bmax.x - eps) { pos.x = bmax.x - eps; vel.x *= -params.boundaryDamping; }
  if (pos.y < bmin.y + eps) { pos.y = bmin.y + eps; vel.y *= -params.boundaryDamping; }
  if (pos.y > bmax.y - eps) { pos.y = bmax.y - eps; vel.y *= -params.boundaryDamping; }
  if (pos.z < bmin.z + eps) { pos.z = bmin.z + eps; vel.z *= -params.boundaryDamping; }
  if (pos.z > bmax.z - eps) { pos.z = bmax.z - eps; vel.z *= -params.boundaryDamping; }

  // Write back
  positionMass[idx] = vec4<f32>(pos, positionMass[idx].w);
  velocityAge[idx] = vec4<f32>(vel, velocityAge[idx].w);

  // Clear forces for next frame
  forceVmax[idx] = vec4<f32>(0.0, 0.0, 0.0, length(vel));
}
`;

    const integrateShader = this.derleShader('integrate', integrateCode);
    this.integratePipeline = this.device.createComputePipeline({
      label: 'SPH Integration Pipeline',
      layout: pipelineLayout,
      compute: { module: integrateShader, entryPoint: 'integrate' }
    });
  }

  /**
   * Compile and cache shader modules
   */
  private derleShader(key: string, code: string): GPUShaderModule {
    if (this.shaderCache.has(key)) {
      return this.shaderCache.get(key)!;
    }

    const module = this.device!.createShaderModule({
      label: `SPH Shader - ${key}`,
      code
    });

    this.shaderCache.set(key, module);
    return module;
  }

  /**
   * Update uniform buffer with simulation parameters
   */
  guncelleUniformlar(config: SPHSimulasyonConfig, zaman: number, activeCount?: number): void {
    if (!this.device || !this.uniformBuffer) return;

    const g = config.global;
    const view = new DataView(this.uniformData);
    let offset = 0;

    // Offset 0-15: gravity_xyz + dt
    view.setFloat32(offset, g.gravity.x, true); offset += 4;
    view.setFloat32(offset, g.gravity.y, true); offset += 4;
    view.setFloat32(offset, g.gravity.z, true); offset += 4;
    view.setFloat32(offset, g.dt, true); offset += 4;

    // Offset 16-31: restDensity, viscosity, surfaceTension, boundaryDamping
    view.setFloat32(offset, g.restDensity, true); offset += 4;
    view.setFloat32(offset, g.viscosity, true); offset += 4;
    view.setFloat32(offset, g.surfaceTension, true); offset += 4;
    view.setFloat32(offset, g.boundaryDamping, true); offset += 4;

    // Offset 32-47: smoothingRadius, particleRadius, maxParticle, activeCount
    view.setFloat32(offset, g.smoothingRadius, true); offset += 4;
    view.setFloat32(offset, g.particleRadius, true); offset += 4;
    view.setUint32(offset, this.maxParticle, true); offset += 4;
    view.setUint32(offset, activeCount || 0, true); offset += 4;

    // Precompute kernel constants
    const h = g.smoothingRadius;
    const h2 = h * h;
    const h3 = h2 * h;
    const h6 = h3 * h3;
    const h9 = h6 * h3;
    const poly6Const = 315.0 / (64.0 * Math.PI * h9);
    const spikyGradConst = -45.0 / (Math.PI * h6);
    const viscLapConst = 45.0 / (Math.PI * h6);
    const cohesionConst = 32.0 / (Math.PI * h9);

    // Offset 48-63: kernel constants
    view.setFloat32(offset, poly6Const, true); offset += 4;
    view.setFloat32(offset, spikyGradConst, true); offset += 4;
    view.setFloat32(offset, viscLapConst, true); offset += 4;
    view.setFloat32(offset, cohesionConst, true); offset += 4;

    // Offset 64-79: gridOrigin_xyz, gridCellSize
    view.setFloat32(offset, g.simBounds.min.x, true); offset += 4;
    view.setFloat32(offset, g.simBounds.min.y, true); offset += 4;
    view.setFloat32(offset, g.simBounds.min.z, true); offset += 4;
    view.setFloat32(offset, g.smoothingRadius * 2.0, true); offset += 4;

    // Offset 80-95: gridDim_xyz, totalGridCells
    view.setUint32(offset, this.gridDim[0], true); offset += 4;
    view.setUint32(offset, this.gridDim[1], true); offset += 4;
    view.setUint32(offset, this.gridDim[2], true); offset += 4;
    view.setUint32(offset, this.totalGridCells, true); offset += 4;

    // Offset 96-127: element counts (8 types)
    view.setUint32(offset, config.dokumGirisleri?.length || 0, true); offset += 4;
    view.setUint32(offset, config.havaDelikleri?.length || 0, true); offset += 4;
    view.setUint32(offset, config.besleyiciler?.length || 0, true); offset += 4;
    view.setUint32(offset, config.filtreler?.length || 0, true); offset += 4;
    view.setUint32(offset, config.engeller?.length || 0, true); offset += 4;
    view.setUint32(offset, config.cikislar?.length || 0, true); offset += 4;
    view.setUint32(offset, config.sensorler?.length || 0, true); offset += 4;
    view.setUint32(offset, config.termalElemanlar?.length || 0, true); offset += 4;

    // Offset 128-143: termalAktif, gazAktif, heatmapTipi, simZaman
    view.setUint32(offset, g.termalAktif ? 1 : 0, true); offset += 4;
    view.setUint32(offset, g.gazAktif ? 1 : 0, true); offset += 4;
    view.setUint32(offset, 0, true); offset += 4; // heatmapTipi
    view.setFloat32(offset, zaman, true); offset += 4;

    // Offset 144-159: solverTipi, cflFactor, havaBasinci, simHizi
    view.setUint32(offset, 0, true); offset += 4; // solverTipi (enum index)
    view.setFloat32(offset, g.cflFactor, true); offset += 4;
    view.setFloat32(offset, g.havaBasinci, true); offset += 4;
    view.setFloat32(offset, g.simHizi, true); offset += 4;

    // Write to GPU
    this.device.queue.writeBuffer(this.uniformBuffer, 0, this.uniformData);
  }

  /**
   * Update element buffers from manager
   */
  guncelleElemanBufferlari(yonetici: ElemanYonetici): void {
    if (!this.device) return;

    for (const [tip, isDirty] of this.elemanDirtyFlags.entries()) {
      if (isDirty) {
        const buffer = this.elemanBuffers.get(tip);
        if (buffer) {
          const gpuData = yonetici.tipGPUBufferOlustur(tip);
          if (gpuData && gpuData.byteLength > 0) {
            this.device.queue.writeBuffer(buffer, 0, gpuData);
          }
        }
        this.elemanDirtyFlags.set(tip, false);
      }
    }
  }

  /**
   * Encode complete simulation frame
   */
  encodeSimulasyonFrame(encoder: GPUCommandEncoder, activeCount: number): void {
    if (!this.hazir || !this.aktivSolver || !this.uniformBG || !this.particleBG || !this.gridBG) return;

    const particleWG = hesaplaWorkgroupSayisi(activeCount, 256);
    const cellWG = hesaplaWorkgroupSayisi(this.totalGridCells, 256);

    // 1. Rebuild spatial grid
    this.encodeGridRebuild(encoder, particleWG, cellWG);

    // 2. Run solver passes (6-arg ISPHSolver interface)
    const solverBG = this.solverBindGroup;
    if (solverBG) {
      this.aktivSolver.encodeSolverPasslari(
        encoder,
        this.uniformBG,
        this.particleBG,
        this.gridBG,
        solverBG,
        particleWG
      );
    }

    // 3. Integration (if solver doesn't handle it)
    if (!this.aktivSolver.kendiIntegrasyonuVar && this.integratePipeline) {
      const pass = encoder.beginComputePass({ label: 'SPH Integration' });
      pass.setPipeline(this.integratePipeline);
      pass.setBindGroup(0, this.uniformBG);
      pass.setBindGroup(1, this.particleBG);
      pass.setBindGroup(2, this.gridBG);
      pass.dispatchWorkgroups(particleWG);
      pass.end();
    }

    // 4. SDF kap çarpışması (kap yüklendiyse) — parçacıkları kavite içinde tutar
    if (this.sdfCollisionPipeline && this.sdfBG) {
      const pass = encoder.beginComputePass({ label: 'SPH SDF Collision' });
      pass.setPipeline(this.sdfCollisionPipeline);
      pass.setBindGroup(0, this.uniformBG);
      pass.setBindGroup(1, this.particleBG);
      pass.setBindGroup(2, this.gridBG);
      pass.setBindGroup(3, this.sdfBG);
      pass.dispatchWorkgroups(particleWG);
      pass.end();
    }
  }

  /**
   * Encode grid rebuild passes
   */
  private encodeGridRebuild(encoder: GPUCommandEncoder, particleWG: number, cellWG: number): void {
    // 1. Hash particles
    const hashPass = encoder.beginComputePass({ label: 'SPH Grid Hash' });
    hashPass.setPipeline(this.gridHashPipeline!);
    hashPass.setBindGroup(0, this.uniformBG!);
    hashPass.setBindGroup(1, this.particleBG!);
    hashPass.setBindGroup(2, this.gridBG!);
    hashPass.dispatchWorkgroups(particleWG);
    hashPass.end();

    // 2. Clear cell counts
    const clearPass = encoder.beginComputePass({ label: 'SPH Grid Clear' });
    clearPass.setPipeline(this.gridClearPipeline!);
    clearPass.setBindGroup(0, this.uniformBG!);
    clearPass.setBindGroup(1, this.particleBG!);
    clearPass.setBindGroup(2, this.gridBG!);
    clearPass.dispatchWorkgroups(cellWG);
    clearPass.end();

    // 3. Count particles per cell
    const countPass = encoder.beginComputePass({ label: 'SPH Grid Count' });
    countPass.setPipeline(this.gridCountPipeline!);
    countPass.setBindGroup(0, this.uniformBG!);
    countPass.setBindGroup(1, this.particleBG!);
    countPass.setBindGroup(2, this.gridBG!);
    countPass.dispatchWorkgroups(particleWG);
    countPass.end();

    // 4. Prefix sum local
    const prefixLocalPass = encoder.beginComputePass({ label: 'SPH Prefix Sum Local' });
    prefixLocalPass.setPipeline(this.gridPrefixSumLocalPipeline!);
    prefixLocalPass.setBindGroup(0, this.uniformBG!);
    prefixLocalPass.setBindGroup(1, this.particleBG!);
    prefixLocalPass.setBindGroup(2, this.gridBG!);
    prefixLocalPass.dispatchWorkgroups(cellWG);
    prefixLocalPass.end();

    // 5. Prefix sum blocks
    const numBlocks = Math.ceil(this.totalGridCells / 256);
    if (numBlocks > 1) {
      const blockWG = hesaplaWorkgroupSayisi(numBlocks, 256);
      const prefixBlocksPass = encoder.beginComputePass({ label: 'SPH Prefix Sum Blocks' });
      prefixBlocksPass.setPipeline(this.gridPrefixSumBlocksPipeline!);
      prefixBlocksPass.setBindGroup(0, this.uniformBG!);
      prefixBlocksPass.setBindGroup(1, this.particleBG!);
      prefixBlocksPass.setBindGroup(2, this.gridBG!);
      prefixBlocksPass.dispatchWorkgroups(blockWG);
      prefixBlocksPass.end();

      // 6. Prefix sum add
      const prefixAddPass = encoder.beginComputePass({ label: 'SPH Prefix Sum Add' });
      prefixAddPass.setPipeline(this.gridPrefixSumAddPipeline!);
      prefixAddPass.setBindGroup(0, this.uniformBG!);
      prefixAddPass.setBindGroup(1, this.particleBG!);
      prefixAddPass.setBindGroup(2, this.gridBG!);
      prefixAddPass.dispatchWorkgroups(cellWG);
      prefixAddPass.end();
    }

    // 7. Copy to offset
    const copyPass = encoder.beginComputePass({ label: 'SPH Grid Copy Offset' });
    copyPass.setPipeline(this.gridCopyOffsetPipeline!);
    copyPass.setBindGroup(0, this.uniformBG!);
    copyPass.setBindGroup(1, this.particleBG!);
    copyPass.setBindGroup(2, this.gridBG!);
    copyPass.dispatchWorkgroups(cellWG);
    copyPass.end();

    // 8. Scatter particles
    const scatterPass = encoder.beginComputePass({ label: 'SPH Grid Scatter' });
    scatterPass.setPipeline(this.gridScatterPipeline!);
    scatterPass.setBindGroup(0, this.uniformBG!);
    scatterPass.setBindGroup(1, this.particleBG!);
    scatterPass.setBindGroup(2, this.gridBG!);
    scatterPass.dispatchWorkgroups(particleWG);
    scatterPass.end();
  }

  /**
   * Change active solver
   */
  async solverDegistir(yeniTip: SolverTipi): Promise<void> {
    if (!this.device || !this.uniformBGL) return;

    // Clean old solver
    if (this.aktivSolver) {
      this.aktivSolver.temizle();
    }
    if (this.solverBuffers) {
      this.solverBuffers.dispose();
      this.solverBuffers = null;
    }
    this.solverBindGroup = null;

    // Create new solver (no device in constructor - all solvers get device via basla())
    this.aktivSolver = SolverFabrika.olustur(yeniTip);

    // Initialize solver: basla() → olusturBufferlar() → olusturBindGroup()
    try {
      await this.aktivSolver.basla(this.device, this.maxParticle, this.uniformBGL);
      this.solverBuffers = this.aktivSolver.olusturBufferlar(this.device, this.maxParticle);
      this.solverBindGroup = this.aktivSolver.olusturBindGroup(this.device, this.solverBuffers);
    } catch (e) {
      console.error('Solver baslatilamadi:', yeniTip, e);
      this.aktivSolver = null;
      return;
    }
  }

  /**
   * Resize buffers for more particles
   */
  bufferBuyut(yeniMax: number): void {
    if (yeniMax <= this.maxParticle || !this.device) return;

    // Save old buffers for copy
    const oldPos = this.positionMassBuffer;
    const oldVel = this.velocityAgeBuffer;
    const oldDens = this.densityMiscBuffer;
    const oldForce = this.forceVmaxBuffer;

    // Create new larger buffers
    const oldMax = this.maxParticle;
    this.maxParticle = yeniMax;
    this.olusturBufferlar(yeniMax);

    // Copy old data
    const encoder = this.device.createCommandEncoder({ label: 'SPH Buffer Resize' });
    if (oldPos) encoder.copyBufferToBuffer(oldPos, 0, this.positionMassBuffer!, 0, oldMax * 16);
    if (oldVel) encoder.copyBufferToBuffer(oldVel, 0, this.velocityAgeBuffer!, 0, oldMax * 16);
    if (oldDens) encoder.copyBufferToBuffer(oldDens, 0, this.densityMiscBuffer!, 0, oldMax * 16);
    if (oldForce) encoder.copyBufferToBuffer(oldForce, 0, this.forceVmaxBuffer!, 0, oldMax * 16);
    this.device.queue.submit([encoder.finish()]);

    // Destroy old buffers
    oldPos?.destroy();
    oldVel?.destroy();
    oldDens?.destroy();
    oldForce?.destroy();

    // Rebuild bind groups
    this.olusturBindGrouplar();

    // Rebuild solver buffers
    if (this.aktivSolver && this.device) {
      if (this.solverBuffers) {
        this.solverBuffers.dispose();
      }
      this.solverBuffers = this.aktivSolver.olusturBufferlar(this.device, yeniMax);
      this.solverBindGroup = this.aktivSolver.olusturBindGroup(this.device, this.solverBuffers);
    }
  }

  /**
   * Reset simulation state (clear particles, keep config)
   */
  sifirla(): void {
    if (!this.device) return;

    // Zero out particle buffers
    const zeroData = new Uint8Array(this.maxParticle * 16);
    if (this.positionMassBuffer) this.device.queue.writeBuffer(this.positionMassBuffer, 0, zeroData);
    if (this.velocityAgeBuffer) this.device.queue.writeBuffer(this.velocityAgeBuffer, 0, zeroData);
    if (this.densityMiscBuffer) this.device.queue.writeBuffer(this.densityMiscBuffer, 0, zeroData);
    if (this.forceVmaxBuffer) this.device.queue.writeBuffer(this.forceVmaxBuffer, 0, zeroData);

    // Zero atomics
    if (this.atomicsBuffer) {
      this.device.queue.writeBuffer(this.atomicsBuffer, 0, new Uint8Array(32));
    }
  }

  /**
   * Upload SDF data for container collision.
   * Info buffer da verilirse çarpışma pipeline'ı + bind group kurulur ve
   * her frame integrate sonrası SDF çarpışması uygulanır.
   */
  kapSDFYukle(sdfBuffer: GPUBuffer, sdfInfoBuffer?: GPUBuffer): void {
    this.sdfBuffer = sdfBuffer;
    this.sdfInfoBuffer = sdfInfoBuffer ?? this.sdfInfoBuffer;
    console.log('[FikoSPHGPUHesap] SDF buffer bağlandı, boyut:', sdfBuffer.size);
    if (this.device && this.sdfInfoBuffer) this.kurSdfCollision();
  }

  /** SDF çarpışma pipeline'ı + bind group (lazy — SDF yüklenince) */
  private kurSdfCollision(): void {
    if (!this.device || !this.sdfBuffer || !this.sdfInfoBuffer
      || !this.uniformBGL || !this.particleBGL || !this.gridBGL) return;

    if (!this.sdfBGL) {
      this.sdfBGL = this.device.createBindGroupLayout({
        label: 'SPH SDF BGL',
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } }, // sdfData
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } }, // sdfInfo
        ],
      });
    }
    if (!this.sdfCollisionPipeline) {
      const layout = this.device.createPipelineLayout({
        label: 'SPH SDF Collision Layout',
        bindGroupLayouts: [this.uniformBGL, this.particleBGL, this.gridBGL, this.sdfBGL],
      });
      const module = this.derleShader('sdfCollision', SDF_COLLISION_PASS);
      this.sdfCollisionPipeline = this.device.createComputePipeline({
        label: 'SPH SDF Collision Pipeline',
        layout,
        compute: { module, entryPoint: 'sdfCollision' },
      });
    }
    this.sdfBG = this.device.createBindGroup({
      label: 'SPH SDF BG',
      layout: this.sdfBGL,
      entries: [
        { binding: 0, resource: { buffer: this.sdfBuffer } },
        { binding: 1, resource: { buffer: this.sdfInfoBuffer } },
      ],
    });
  }

  /**
   * Read sensor data from GPU (async readback)
   */
  async sensorOku(sensorId: string): Promise<any> {
    if (!this.device || !this.sensorReadbackBuffer) return null;

    try {
      await this.sensorReadbackBuffer.mapAsync(GPUMapMode.READ);
      const data = new Float32Array(this.sensorReadbackBuffer.getMappedRange().slice(0));
      this.sensorReadbackBuffer.unmap();
      return data;
    } catch (e) {
      return null;
    }
  }

  /**
   * Cleanup all GPU resources
   */
  temizle(): void {
    // Destroy particle buffers
    this.positionMassBuffer?.destroy();
    this.velocityAgeBuffer?.destroy();
    this.densityMiscBuffer?.destroy();
    this.forceVmaxBuffer?.destroy();

    // Destroy grid buffers
    this.cellCountBuffer?.destroy();
    this.prefixSumBuffer?.destroy();
    this.cellOffsetBuffer?.destroy();
    this.sortedIndexBuffer?.destroy();
    this.cellHashBuffer?.destroy();
    this.blockSumsBuffer?.destroy();

    // Destroy element buffers
    for (const buffer of this.elemanBuffers.values()) {
      buffer.destroy();
    }
    this.elemanBuffers.clear();
    this.elemanDirtyFlags.clear();

    // Destroy other buffers
    this.uniformBuffer?.destroy();
    this.indirectDispatchBuffer?.destroy();
    this.atomicsBuffer?.destroy();
    this.sensorResultBuffer?.destroy();
    this.sensorReadbackBuffer?.destroy();
    this.statisticsBuffer?.destroy();
    this.statisticsReadbackBuffer?.destroy();
    this.heatmapBuffer?.destroy();
    this.particleSicaklikBuffer?.destroy();
    this.particleFazBuffer?.destroy();
    this.kabarcikBoyutBuffer?.destroy();
    this.kabarcikFlagsBuffer?.destroy();

    // Clean solver
    if (this.aktivSolver) {
      this.aktivSolver.temizle();
      this.aktivSolver = null;
    }

    // Clear caches
    this.shaderCache.clear();

    // SDF referansları (buffer sahipliği dışarıda — destroy edilmez)
    this.sdfBuffer = null;
    this.sdfInfoBuffer = null;
    this.sdfBG = null;
    this.sdfBGL = null;
    this.sdfCollisionPipeline = null;

    // Reset state
    this.hazir = false;
    this.device = null;
  }

  // Getters
  getPositionBuffer(): GPUBuffer | null { return this.positionMassBuffer; }
  getVelocityBuffer(): GPUBuffer | null { return this.velocityAgeBuffer; }
  getDensityBuffer(): GPUBuffer | null { return this.densityMiscBuffer; }
  getHeatmapBuffer(): GPUBuffer | null { return this.heatmapBuffer; }
  isHazir(): boolean { return this.hazir; }
  getDevice(): GPUDevice | null { return this.device; }
  getUniformBindGroup(): GPUBindGroup | null { return this.uniformBG; }
  getParticleBindGroup(): GPUBindGroup | null { return this.particleBG; }
  getGridBindGroup(): GPUBindGroup | null { return this.gridBG; }
  getMaxParticle(): number { return this.maxParticle; }
  getGridDim(): [number, number, number] { return this.gridDim; }
  elemanBufferDirty(tip: ElemanTipi, dirty: boolean = true): void {
    this.elemanDirtyFlags.set(tip, dirty);
  }

  /**
   * Spawn particles from CPU-side arrays
   * Writes to GPU buffers at given startIndex
   * @param startIndex - Starting particle index
   * @param positions - Packed xyz array [x,y,z, x,y,z, ...]
   * @param velocities - Packed vxvyvz array [vx,vy,vz, vx,vy,vz, ...]
   * @param mass - Mass per particle (default 1.0)
   * @returns Number of particles written
   */
  cpuSpawnParcaciklar(startIndex: number, positions: Float32Array, velocities: Float32Array, mass: number = 1.0): number {
    if (!this.device || !this.positionMassBuffer || !this.velocityAgeBuffer || !this.densityMiscBuffer) {
      return 0;
    }

    const particleCount = positions.length / 3;
    if (particleCount <= 0 || velocities.length / 3 !== particleCount) {
      return 0;
    }

    const invMass = mass > 0 ? 1.0 / mass : 1.0;

    // Build positionMass buffer: [x, y, z, inverseMass] per particle
    const positionMassData = new Float32Array(particleCount * 4);
    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      const i4 = i * 4;
      positionMassData[i4 + 0] = positions[i3 + 0];
      positionMassData[i4 + 1] = positions[i3 + 1];
      positionMassData[i4 + 2] = positions[i3 + 2];
      positionMassData[i4 + 3] = invMass;
    }

    // Build velocityAge buffer: [vx, vy, vz, age=0] per particle
    const velocityAgeData = new Float32Array(particleCount * 4);
    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      const i4 = i * 4;
      velocityAgeData[i4 + 0] = velocities[i3 + 0];
      velocityAgeData[i4 + 1] = velocities[i3 + 1];
      velocityAgeData[i4 + 2] = velocities[i3 + 2];
      velocityAgeData[i4 + 3] = 0.0; // age
    }

    // Build densityMisc buffer: [restDensity=1000, 0, 0, sourceId=0] per particle
    const densityMiscData = new Float32Array(particleCount * 4);
    for (let i = 0; i < particleCount; i++) {
      const i4 = i * 4;
      densityMiscData[i4 + 0] = 1000.0; // restDensity
      densityMiscData[i4 + 1] = 0.0;
      densityMiscData[i4 + 2] = 0.0;
      densityMiscData[i4 + 3] = 0.0; // sourceId
    }

    // Write to GPU buffers
    const offset = startIndex * 16; // 16 bytes per particle (4 floats)
    this.device.queue.writeBuffer(this.positionMassBuffer, offset, positionMassData);
    this.device.queue.writeBuffer(this.velocityAgeBuffer, offset, velocityAgeData);
    this.device.queue.writeBuffer(this.densityMiscBuffer, offset, densityMiscData);

    return particleCount;
  }

  /**
   * Spawn a cube of test particles for quick visualization testing
   * Returns number of spawned particles
   */
  cpuSpawnTestKup(startIndex: number, center: [number, number, number], boyut: number, aralık: number, hiz: [number, number, number] = [0, 0, 0]): number {
    // Generate positions in a cube grid
    const half = boyut / 2;
    const positions: number[] = [];
    const velocities: number[] = [];

    for (let x = -half; x < half; x += aralık) {
      for (let y = -half; y < half; y += aralık) {
        for (let z = -half; z < half; z += aralık) {
          positions.push(center[0] + x, center[1] + y, center[2] + z);
          velocities.push(hiz[0], hiz[1], hiz[2]);
        }
      }
    }

    return this.cpuSpawnParcaciklar(
      startIndex,
      new Float32Array(positions),
      new Float32Array(velocities)
    );
  }
}

// Export
export default FikoSPHGPUHesap;
